import { cn } from "@/lib/utils";
import { celdaVigencia, type EntradaCeldaVigencia } from "@/lib/celda-vigencia";
import { Icon } from "./Icon";
import { Tip } from "./Tip";

export interface CeldaVigenciaProps extends EntradaCeldaVigencia {
  /**
   * Qué documento es. Va al `aria-label` y al tooltip, y no es opcional: el
   * botón se anuncia leyendo su contenido ("23 nov 2026, en 119 días · 2
   * avisos"), que describe el ESTADO y no lo que pasa al apretarlo.
   */
  label: string;
  /** Abre el modal de vigencia. Sin esto la celda se dibuja inerte. */
  onEditar?: () => void;
  /** Hay una operación en curso sobre esta fila. */
  ocupada?: boolean;
  className?: string;
}

/**
 * Celda de vencimiento del Design System (§4bis.1).
 *
 * Dos líneas y SIEMPRE las mismas:
 *
 *   línea 1: la fecha en Geist Mono + `edit_calendar` teal si es editable
 *   línea 2: la campana + "en 224 días · 2 avisos"
 *
 * Nunca se separan vencimiento y recordatorios en dos columnas: el usuario
 * decide las dos cosas en el mismo lugar, así que también las lee junto.
 *
 * Los textos y los colores salen de `celdaVigencia()` — ver ahí por qué hay un
 * solo generador y qué decía cada una de las cuatro copias que reemplaza.
 */
export function CeldaVigencia({
  label,
  onEditar,
  ocupada = false,
  className,
  ...entrada
}: CeldaVigenciaProps) {
  const celda = celdaVigencia(entrada);
  const clickeable = celda.editable && onEditar !== undefined;

  const contenido = (
    <>
      <span className="flex min-w-0 items-center gap-1.5">
        <span
          className={cn(
            "truncate text-xs",
            // Geist Mono sólo cuando es un DATO. "No vence" y "No exigible" son
            // prosa: en mono se leen como si fueran un código del sistema.
            celda.estado === "permanente" || celda.estado === "no_exigible"
              ? ""
              : "fc-num",
            celda.fechaTono,
          )}
        >
          {celda.fecha}
        </span>
        {clickeable &&
          (ocupada ? (
            <Icon
              name="progress_activity"
              size={14}
              className="text-primary animate-fc-spin shrink-0"
            />
          ) : (
            <Icon name="edit_calendar" size={14} className="text-primary shrink-0" />
          ))}
      </span>
      {celda.detalle && (
        <span className={cn("flex min-w-0 items-center gap-1", celda.detalleTono)}>
          {celda.icono && <Icon name={celda.icono} size={12} className="shrink-0" />}
          <span className="truncate text-[10.5px]">{celda.detalle}</span>
        </span>
      )}
    </>
  );

  if (!clickeable) {
    // Inerte: sin cursor, sin hover y sin ícono de edición. El DS es explícito
    // — en "No exigible" la celda no invita a nada porque no hay nada que
    // configurar.
    return (
      <div className={cn("flex min-w-0 flex-col items-start gap-px", className)}>{contenido}</div>
    );
  }

  return (
    <Tip texto="Editar vencimiento y recordatorios" claseEnvoltorio="min-w-0">
      <button
        type="button"
        onClick={onEditar}
        disabled={ocupada}
        aria-label={`Editar el vencimiento y los recordatorios de ${label}`}
        // El margen negativo compensa el padding para que la primera línea
        // quede alineada con el resto de la columna: sin eso, la celda entera
        // aparece corrida siete píxeles a la derecha de su encabezado.
        className={cn(
          "hover:bg-primary-50 -ml-[7px] flex min-w-0 flex-col items-start gap-px rounded-md px-[7px] py-1 text-left transition-colors disabled:opacity-60",
          className,
        )}
      >
        {contenido}
      </button>
    </Tip>
  );
}
